import React, { useEffect, useMemo, useState } from 'react'
import { PropellerTypeConstant, VesselMachineryConstant } from 'shared/constants';
import { queryKeyes } from 'shared/queryKeys';
import { commonValidationMessages } from 'Components/ValidationErrorMessages';
import * as Yup from "yup";
import ErrorComponent from 'Components/ErrorComponent';
import { Formik } from 'formik';
import env from 'environment_system/env_system';
import { checkValidArray, handleServerResponse, useSafeFormikContext } from 'GenericForms/Helper';
import apiGlobal from 'global/api.global';
import ParametersStructure from './ParametersStructure';
import { RootState } from 'index';
import { useSelector, useDispatch } from 'react-redux';
import { getCurrentReportAction } from 'Store/Generic/ReportingSlice';
import FormLoading from 'Components/FormLoading';
import { useEditReport } from 'VesselReportingInfo/EditCompletedReports/EditReportProvider';

const ParametersComponent = ({
    toggleDynamicTabs,
    setErrorMessage,
}: any) => {
    /** State variables start */
    const { previousEnggTabIndex } = useEditReport();
    const { VesselID, ReportID, VoyageID, CurrentReport, reportingLoading, ConfigurationLimits, reportingError, PreviousReport } = useSelector((state: RootState) => state.Reporting);
    const dispatch = useDispatch();
    const currentVesselReport = CurrentReport?.data?.vessel_reporting_information ?? null;
    const voyageParameterCurrentReport = CurrentReport?.data?.chief_engineer?.voyage_parameter ?? null;
    const energyParameterCurrentReport = CurrentReport?.data?.chief_engineer?.energy_parameter ?? null;
    const [refreshKey, setRefreshKey] = useState(0);
    const [vesselEngines, setVesselEngines] = useState<Array<any>>([]);
    const formikContext = useSafeFormikContext<any>()
    const editValues = formikContext?.values;
    const setEditValues = formikContext?.setValues;
    const editInitialValues = formikContext?.initialValues;
    const editSubmit = formikContext?.submitForm;
    const editIsSubmitting = formikContext?.isSubmitting;
    const [voyage_modal_backdrop, set_voyage_modal_backdrop] = useState(false);
    const [energy_modal_backdrop, set_energy_modal_backdrop] = useState(false);
    const vesselMachineries = useMemo(
        () => ConfigurationLimits?.vessel_machinery_list ?? [],
        [ConfigurationLimits?.vessel_machinery_list]
    );
    const mainEngineSetting = useMemo(
        () => ConfigurationLimits?.main_engine_setting ?? [],
        [ConfigurationLimits?.main_engine_setting]
    );
    /**useEffect start */
    /** Create and assign initial object to Formik's initial values' object  */
    useEffect(() => {
        ParameterFormik.initialValues.voyageParameter = getInitialVoyageParameterValues();
        ParameterFormik.initialValues.energyParameter = getInitialEneryParameterValues();
        setRefreshKey(refreshKey + 1);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [vesselMachineries, mainEngineSetting, voyageParameterCurrentReport, vesselEngines, energyParameterCurrentReport,
        editInitialValues?.chief_engineer?.voyage_parameter, editInitialValues?.chief_engineer?.energy_parameter,
    ]);
    /**Get vessel's main engines */
    useEffect(() => {
        if (checkValidArray(vesselMachineries)) {
            setVesselEngines(
                vesselMachineries.filter(
                    (item: any) =>
                        item.machinery_precedence_id === VesselMachineryConstant.MAIN_ENGINE
                )
            );
            // queryClient.invalidateQueries(queryKeyes.vessel.MainEngineSetting.key);
            // queryClient.invalidateQueries(queryKeyes.vessel.VoyageParameterObject.key);
        }
    }, [vesselMachineries]);
    /**useEffect end */

    /** Assign values to initial object of Voyage Parameter */
    const getInitialVoyageParameterValues = () => {
        let obj: any[] = [];
        if (checkValidArray(editInitialValues?.chief_engineer?.voyage_parameter)) {
            return editInitialValues?.chief_engineer?.voyage_parameter;
        } else if (checkValidArray(voyageParameterCurrentReport)) {
            vesselEngines.forEach((engine: any) => {
                if (voyageParameterCurrentReport.some((voyage: any) => engine.id === voyage.vessel_machinery)) {
                    obj.push(voyageParameterCurrentReport.filter((obj: any) => obj.vessel_machinery === engine.id)[0])
                } else {
                    obj.push({
                        set_rpm: null,
                        avg_rpm: null,
                        slip_percentage: null,
                        total_revolutions: null,
                        propeller_pitch:
                            mainEngineSetting &&
                            mainEngineSetting
                                .filter((item: any) => item.vessel_machinery === engine.id)
                                .map((setting: any) => {
                                    if (setting.propeller_type === PropellerTypeConstant.FPP) {
                                        return setting.propeller_pitch;
                                    } else {
                                        return null;
                                    }
                                })[0],
                        vessel_machinery: engine.id,
                        is_revolution_counter_not_functional: false,
                        vessel: VesselID,
                        voyage_information: VoyageID,
                        vessel_reporting_information: ReportID,
                    })
                }
            })
            return obj;
        } else if (
            mainEngineSetting !== undefined &&
            vesselMachineries !== undefined &&
            obj.length <= vesselEngines.length
        ) {
            vesselMachineries
                .filter(
                    (item: any) =>
                        item.machinery_precedence_id === VesselMachineryConstant.MAIN_ENGINE
                ).forEach((machine: any) => {
                    const matchedSetting = mainEngineSetting?.find((setting: any) => setting.vessel_machinery === machine.id);
                    const matchedVoyageParam = PreviousReport?.data?.chief_engineer?.voyage_parameter?.find((param: any) => param.vessel_machinery === machine.id);
                    obj.push({
                        set_rpm: null,
                        avg_rpm: null,
                        slip_percentage: null,
                        total_revolutions: null,
                        propeller_pitch:
                            matchedSetting?.propeller_type === PropellerTypeConstant.FPP
                                ? matchedSetting.propeller_pitch
                                : null,
                        vessel_machinery: machine.id,
                        is_revolution_counter_not_functional:
                            matchedVoyageParam?.is_revolution_counter_not_functional ?? false,
                        vessel: VesselID,
                        voyage_information: VoyageID,
                        vessel_reporting_information: ReportID,
                    });
                });
            return obj;
        } else {
            return [
                {
                    set_rpm: 0,
                    avg_rpm: 0,
                    slip_percentage: 0,
                    total_revolutions: 0,
                    propeller_pitch: 0,
                    vessel_machinery: 0,
                    is_revolution_counter_not_functional: false,
                    vessel: VesselID,
                    voyage_information: VoyageID,
                    vessel_reporting_information: ReportID,
                },
            ];
        }
    };

    /** Assign values to initial object of Energy Parameter */
    const getInitialEneryParameterValues = () => {
        let obj: any[] = [];
        if (checkValidArray(editInitialValues?.chief_engineer?.energy_parameter)) {
            return editInitialValues?.chief_engineer?.energy_parameter;
        } else if (checkValidArray(energyParameterCurrentReport)) {
            vesselMachineries?.filter((item: any) => item.energy_parameter === true).forEach((machine: any) => {
                if (energyParameterCurrentReport.some((energy: any) => machine.id === energy.vessel_machinery)) {
                    obj.push(energyParameterCurrentReport.filter((obj: any) => obj.vessel_machinery === machine.id)[0])
                } else {
                    obj.push({
                        energy_developed: null,
                        running_hours: null,
                        avg_power: null,
                        is_energy_meter_functional: true,
                        vessel_machinery: machine.id,
                        vessel: VesselID,
                        voyage_information: VoyageID,
                        vessel_reporting_information: ReportID,
                    });
                }
            });
            return obj;
        } else if (
            vesselMachineries !== undefined &&
            obj.length <= vesselMachineries.filter((item: any) => item.energy_parameter === true).length
        ) {
            vesselMachineries
                .filter((item: any) => item.energy_parameter === true)
                .map((vesselMachinery: any) => {
                    const matchedEnergyParam = PreviousReport?.data?.chief_engineer?.energy_parameter?.find((param: any) => param.vessel_machinery === vesselMachinery.id);
                    obj.push({
                        energy_developed: null,
                        running_hours: null,
                        avg_power: null,
                        is_energy_meter_functional: matchedEnergyParam?.is_energy_meter_functional ?? true,
                        vessel_machinery: vesselMachinery.id,
                        vessel: VesselID,
                        voyage_information: VoyageID,
                        vessel_reporting_information: ReportID,
                    });
                    return vesselMachinery;
                });
            return obj;
        } else {
            return [
                {
                    energy_developed: 0,
                    running_hours: 0,
                    avg_power: 0,
                    is_energy_meter_functional: true,
                    vessel_machinery: 0,
                    vessel: VesselID,
                    voyage_information: VoyageID,
                    vessel_reporting_information: ReportID,
                },
            ];
        }
    };

    const ParameterFormik = {
        initialValues: {
            voyageParameter: getInitialVoyageParameterValues(),
            energyParameter: getInitialEneryParameterValues(),
        },
        validationSchema: env?.form_validation ? Yup.object().shape({
            voyageParameter: Yup.array(
                Yup.object({
                    set_rpm: Yup.string()
                        .matches(
                            /^\d{0,3}(?:\.\d{1,4})?$/,
                            `${commonValidationMessages.before3after4}`
                        )
                        .required(commonValidationMessages.required),
                    is_revolution_counter_not_functional: Yup.boolean(),
                    avg_rpm: Yup.string()
                        .when("is_revolution_counter_not_functional", {
                            is: true,
                            then: (schema: any) =>
                                schema.matches(
                                    /^\d{0,5}(?:\.\d{1,4})?$/,
                                    `${commonValidationMessages.before5after4}`
                                ).required(commonValidationMessages.required),
                            otherwise: (schema: any) => schema.nullable(),
                        }),
                    total_revolutions: Yup.string()
                        .when("is_revolution_counter_not_functional", {
                            is: false,
                            then: (schema: any) =>
                                schema.matches(
                                    /^\d{0,6}(?:\.\d{1,4})?$/,
                                    `${commonValidationMessages.before6after4}`
                                ).required(commonValidationMessages.required),
                            otherwise: (schema: any) => schema.nullable(),
                        }),
                    propeller_pitch: Yup.string()
                        .matches(
                            /^(?!.*\s)\d{1,2}(\.\d{1,10})?$/,
                            commonValidationMessages.before2after10
                        )
                        .required(commonValidationMessages.required)
                })
            ),
            energyParameter: Yup.array(
                Yup.object({
                    energy_developed: Yup.string()
                        .when("is_energy_meter_functional", {
                            is: true,
                            then: (schema: any) =>
                                schema.matches(/^(\d{1,7})(\.\d{1,4})?$/, commonValidationMessages.before7after4)
                                    .required(commonValidationMessages.required),
                            otherwise: (schema: any) => schema.nullable(),
                        }),
                    running_hours: Yup.number()
                        .typeError(commonValidationMessages.number)
                        .required(commonValidationMessages.required)
                        .min(0, "Running hours must be at least 0")
                        .max(
                            (currentVesselReport?.time_duration_since_last_report ?? 0) / 60,
                            `Please enter a number with up to ${((currentVesselReport?.time_duration_since_last_report ?? 0) / 60)?.toFixed(2)} hours`
                        )
                        .test(
                            "max-2-decimals",
                            commonValidationMessages.before2after2,
                            (value) => {
                                if (value === undefined || value === null) {
                                    return true
                                };
                                return /^\d+(\.\d{1,2})?$/.test(value.toString());
                            }
                        ),
                    avg_power: Yup.string()
                        .when("is_energy_meter_functional", {
                            is: false,
                            then: (schema: any) =>
                                schema.matches(
                                    /^\d{0,5}(?:\.\d{1,4})?$/,
                                    `${commonValidationMessages.before5after4}`
                                ).required(commonValidationMessages.required),
                            otherwise: (schema: any) => schema.nullable(),
                        }),
                })
            ),
        }) : null,
    }
    return (
        <React.Fragment>
            {reportingLoading && <FormLoading />}
            {reportingError && <ErrorComponent message="Unable to load required data!" />}
            {!reportingLoading && !reportingError && (
                <Formik
                    onSubmit={async (values: any, actions: any) => {
                        if (env?.form_validation === false) {
                            setErrorMessage(null)
                            toggleDynamicTabs(previousEnggTabIndex + 1);
                        }
                        actions.setSubmitting(true);
                        const responseArray: Promise<any>[] = [];
                        /** Voyage Parameter submit */
                        values.voyageParameter.forEach((value: any) => {
                            if (value.id) {
                                responseArray.push(apiGlobal.put(`${queryKeyes.vessel.VoyageParameter.url()}${value.id}/`, value));
                            } else {
                                responseArray.push(apiGlobal.post(`${queryKeyes.vessel.VoyageParameter.url()}`, value));
                            }
                        });
                        /** Energy parameter submit */
                        values.energyParameter.forEach((value: any) => {
                            if (value.id > 0) {
                                responseArray.push(apiGlobal.put(`/energy_parameter/${value.id}/`, value));
                            } else {
                                responseArray.push(apiGlobal.post(`/energy_parameter/`, value));
                            }
                        });
                        const responses = await handleServerResponse(responseArray);
                        if (responses === true) {
                            dispatch(getCurrentReportAction(VesselID, ReportID));
                            setRefreshKey(refreshKey + 1);
                            if (env?.form_validation === true) {
                                setErrorMessage(null)
                                toggleDynamicTabs(previousEnggTabIndex + 1);
                            }
                        } else {
                            setErrorMessage(responses)
                        }
                    }}

                    initialValues={ParameterFormik.initialValues}
                    validationSchema={env?.form_validation === true ? ParameterFormik.validationSchema : null}
                    key={refreshKey}
                >
                    {() =>
                    (
                        <ParametersStructure
                            setErrorMessage={setErrorMessage}
                            toggleDynamicTabs={toggleDynamicTabs}
                            setRefreshKey={setRefreshKey}
                            refreshKey={refreshKey}
                            vesselMachineries={vesselMachineries}
                            mainEngineSetting={mainEngineSetting}
                            vesselEngines={vesselEngines}
                            editValues={editValues}
                            setEditValues={setEditValues}
                            modal_backdrop={voyage_modal_backdrop}
                            energy_modal_backdrop={energy_modal_backdrop}
                            set_energy_modal_backdrop={set_energy_modal_backdrop}
                            set_modal_backdrop={set_voyage_modal_backdrop}
                            editInitialValues={editInitialValues}
                            editSubmit={editSubmit}
                            editIsSubmitting={editIsSubmitting}
                        />
                    )}
                </Formik>
            )}
        </React.Fragment>
    )
}

export default ParametersComponent